import { computed, inject, Injectable, signal } from "@angular/core";
import { SidenavModel } from "../models/sidenav.model";
import { SidenavItemInterface } from "../interfaces/side-nav-items.interface";
import { AuthService } from "@kernel/tools/services/auth.service";
import { PRIMARY_OUTLET, Router } from "@angular/router";
import { tap } from "rxjs";

/**
 * Central state manager for side navigation links, expansion state,
 * route activation, and permission filtering.
 */
@Injectable({
  providedIn: "root",
})
/**
 * Central state manager for the application side navigation tree.
 *
 * It owns sidenav links, selected main link, permission visibility checks,
 * route-based activation, and incremental submenu registration from features.
 */
export class SideNavService {
  private router = inject(Router);
  private authService = inject(AuthService);

  /**
   * Root collection of top-level side-nav links.
   */
  private _mainLinks = signal<SidenavModel[]>([]);

  /**
   * Currently selected top-level link.
   */
  private _selectedMainLink = signal(this._mainLinks().find(link => link.expanded));

  /** Currently selected top-level sidenav item. */
  selectedMainLink = computed(() => this._selectedMainLink());

  /**
   * Synthetic root item used to normalize recursive menu operations.
   */
  private _rootSidenavItem: SidenavModel = new SidenavModel({
    id: "root",
    items: [],
    label: "ROOT",
    icon: "",
  });

  /** Synthetic root node used to aggregate all top-level links. */
  get rootSidenavItem() {
    return this._rootSidenavItem;
  }

  /** Reactive list of top-level links shown in the icon bar. */
  mainLinks = computed<SidenavModel[]>(() => this._mainLinks());

  /**
   * Initializes permission state for currently registered links.
   */
  constructor() {
    this.checkPermissions();
  }

  /**
   * Watches authentication changes and refreshes sidenav visibility/active state.
   *
   * @returns Stream that emits on user changes after sidenav state is updated.
   */
  observeUserAuthorization() {
    return this.authService.user$.pipe(
      tap(() => {
        this.checkPermissions();
        this.updateLinksState();
      })
    );
  }

  /** Re-evaluates current route activation after clearing expanded states. */
  updateLinksState() {
    this.rootSidenavItem.unExpandAllItems();
    this.searchMenuActivatedRoute(this.router.url);
  }

  /** Applies permission visibility checks for all top-level links. */
  checkPermissions() {
    const user = this.authService.getUser();
    this._mainLinks().map(link => link.checkPermissions(user));
  }

  /**
   * Clears selected link and expanded state, typically after logout.
   *
   * TODO: Refactor sidenav to be initialized once at app root.
   */
  resetSideNav() {
    this._selectedMainLink()?.unExpandAllItems();
    this._selectedMainLink.set(undefined);
  }

  /**
   * Sets the initial top-level sidenav links and applies permission checks.
   *
   * @param mainLinks Raw top-level link definitions.
   */
  setMainLinks(mainLinks: SidenavItemInterface[]): void {
    this._mainLinks.set(this.rootSidenavItem.setMainLinks(mainLinks));
    this._mainLinks.update(links => links.map(i => i.checkPermissions(this.authService.getUser())));
  }

  /**
   * Selects a top-level link either by id or by matching a route.
   *
   * @param id Target top-level link id.
   * @param routerLink Route string or route pieces used for best-match selection.
   */
  setSelectedMainLink(id?: string | null, routerLink?: string | string[]): void {
    let selectedMainLink: SidenavModel | undefined;
    if (id) {
      selectedMainLink = this.setSelectMainItemWithId(id);
    } else if (routerLink) {
      routerLink = Array.isArray(routerLink) ? routerLink : routerLink?.split("/");
      selectedMainLink = this.setSelectMainItemWithRouterLink(routerLink);
    }
    this._selectedMainLink.set(selectedMainLink);
    this.refreshLinks();
  }

  /**
   * Publishes in-place mutations of the links tree.
   *
   * Expanded and active flags live on mutable {@link SidenavModel} nodes, so
   * `OnPush` consumers only observe them through a new links reference.
   */
  private refreshLinks(): void {
    this._mainLinks.update(links => [...links]);
  }

  /**
   * Picks the best visible main link matching the provided route.
   *
   * If no route match exists, it prefers the currently expanded visible link,
   * then falls back to the first visible main link.
   *
   * @param routerLink Current route split into path pieces.
   * @returns Selected main link, if any.
   */
  private setSelectMainItemWithRouterLink(routerLink: string[]): SidenavModel | undefined {
    let selectedMainLink = this._mainLinks().find(link => {
      if (link.getMatchRate(routerLink)) {
        return true;
      } else {
        return link.isShown && link.expanded;
      }
    });
    if (!selectedMainLink) {
      selectedMainLink = this._mainLinks().find(link => link.isShown);
      selectedMainLink && (selectedMainLink.expanded = true);
    }
    return selectedMainLink;
  }

  /**
   * Selects a top-level link by id and expands/activates its branch.
   *
   * @param id Target top-level link id.
   * @returns Selected main link, if found.
   */
  private setSelectMainItemWithId(id: string): SidenavModel | undefined {
    const selectedMainLink = this._mainLinks().find(link => link.id === id);
    if (selectedMainLink?.routerLink) this.rootSidenavItem.unExpandAllItems();
    else this.collapsePreviewedMainLinks(id);
    selectedMainLink?.expandItem(!!selectedMainLink?.routerLink);
    return selectedMainLink;
  }

  /**
   * Collapses main links that were only expanded as a preview.
   *
   * Browsing main links without navigating leaves each visited link expanded,
   * so only the last clicked one should keep that state. The link holding the
   * current route is skipped since it is marked active, not previewed, and its
   * open branch must survive until the route actually changes.
   *
   * @param id Id of the main link that keeps its expanded state.
   */
  private collapsePreviewedMainLinks(id: string): void {
    this._mainLinks()
      .filter(link => link.id !== id && !link.isActive)
      .forEach(link => link.collapseAllItems());
  }

  /**
   * Adds or merges submenu items under a top-level link.
   *
   * Matching is done by item id so repeated feature registrations extend
   * existing branches instead of duplicating them.
   *
   * @param id Parent top-level link id.
   * @param items Submenu items to merge under that parent.
   */
  addSubMenuItems(id: string, items: SidenavItemInterface[]): void {
    const mainLink = this._mainLinks().find(link => link.id === id);
    if (!mainLink) return;
    this.mergeSubMenuItems(mainLink, items);
    mainLink.checkPermissions(this.authService.getUser());
    this.refreshLinks();
  }

  /**
   * Recursively merges incoming submenu items into an existing branch.
   *
   * @param parentItem Existing parent node to merge into.
   * @param items Incoming raw items.
   */
  private mergeSubMenuItems(parentItem: SidenavModel, items: SidenavItemInterface[]): void {
    for (const item of items) {
      const existingItem = parentItem.items.find(parentSubItem => parentSubItem.id === item.id);
      if (!existingItem) {
        parentItem.setSubMainLinks([item]);
        continue;
      }

      if (item.items?.length) {
        this.mergeSubMenuItems(existingItem, item.items);
      }
    }
  }

  /**
   * Toggles expansion for a node or activates router links.
   *
   * Only routable items clear the previously active branch, since they are the
   * ones that change the route; grouped items merely collapse their siblings.
   *
   * @param item Clicked sidenav node.
   */
  toggleExpandItem(item: SidenavModel) {
    if (item.expanded) {
      item.collapseAllItems();
    } else {
      if (item.routerLink) this.rootSidenavItem.unExpandAllItems();
      else item.parent?.collapseAllItems();
      item.expandItem(!!item.routerLink);
    }
    this.refreshLinks();
  }

  /**
   * Activates sidenav items based on the current route.
   *
   * @param routerLink Current url as string or already-split route pieces.
   */
  searchMenuActivatedRoute(routerLink: string | string[]): void {
    // const routeArray = Array.isArray(routerLink) ? routerLink : routerLink.split("/");
    let routeArray: string[];
    if (Array.isArray(routerLink)) {
      routeArray = routerLink;
    } else {
      const urlTree = this.router.parseUrl(routerLink);
      const primarySegments = urlTree.root.children[PRIMARY_OUTLET]?.segments || [];
      const path = "/" + primarySegments.map(s => s.path).join("/");
      routeArray = path.split("/");
    }
    this.rootSidenavItem.activateItem(routeArray);
    this.setSelectedMainLink(undefined, routeArray);
  }
}
